import uuidv4 from 'uuid/v4';

import ScribeTextElementModel from '../models/ScribeTextElementModel';
import ScribeImageElementModel from '../models/ScribeImageElementModel';
import ScribeShapeElementModel from '../models/ScribeShapeElementModel';
import { canvasSizeDefault } from '../shared/resources/scribedefaults';
import {
  pauseTime as defaultPauseTime,
  animationTime as defaultAnimationTime
} from '../shared/resources/scribedefaults';
import {
  defaultCanvasColor,
  defaultCanvasColorTo,
  defaultCanvasColorFrom,
  defaultBackgroundType,
  defaultBackgroundGradientType
} from '../config/defaults';
import config from '../config/config';
import blankThumbnailImage from '../../imgs/projthumb-blank.svg';

const getSettingsWithDefaults = (settings = {}) => {
  const pauseTime = settings && typeof settings.pauseTime !== 'undefined' ? settings.pauseTime : defaultPauseTime;
  const animationTime =
    settings && typeof settings.animationTime !== 'undefined' ? settings.animationTime : defaultAnimationTime;
  const backgroundColor = settings.backgroundColor || settings.backgroundColour || defaultCanvasColor;
  const backgroundColorTo = settings.backgroundColorTo || settings.backgroundColourTo || defaultCanvasColorTo;
  const backgroundColorFrom = settings.backgroundColorFrom || settings.backgroundColourFrom || defaultCanvasColorFrom;
  const backgroundType = settings.backgroundType || defaultBackgroundType;
  const backgroundGradientType = settings.backgroundGradientType || defaultBackgroundGradientType;
  const gridlines = settings.gridlines || false;

  return {
    pauseTime,
    animationTime,
    backgroundColor,
    backgroundColorTo,
    backgroundColorFrom,
    backgroundType,
    backgroundGradientType,
    gridlines
  };
};

class ScribeModel {
  constructor({
    id,
    title,
    description,
    canvasSize,
    thumbnailImage,
    createdOn,
    updatedOn,
    rawElements,
    settings,
    audio, // Existing scribes with audio object will be migrated to scribes with audioClips array
    audioClips,
    cursor = 'default',
    source,
    scenes
  } = {}) {
    const now = new Date();
    this.id = id;
    this.title = title;
    this.description = description;
    this.canvasSize = canvasSize || canvasSizeDefault;
    this.thumbnailImage = thumbnailImage !== blankThumbnailImage ? thumbnailImage : undefined;
    this.createdOn = createdOn || now;
    this.updatedOn = updatedOn || now;
    this.settings = getSettingsWithDefaults(settings);
    this.isValid = true;
    this.cursor = cursor;
    this.elements = ScribeModel.mapToElementModel(rawElements);
    this.source = source;

    // Migrate scribe to Scenes
    if (!scenes) {
      this.scenes = [
        {
          id: uuidv4(),
          elementIds: this.elements.map(el => el.id),
          settings: getSettingsWithDefaults(settings),
          active: true,
          // Only migrate Scribe thumbnail images if they are not the default
          thumbnailImage:
            thumbnailImage?.includes('base64') || thumbnailImage?.includes(config.CONTENTFUL_SPACE_ID)
              ? thumbnailImage
              : undefined
        }
      ];
    } else {
      let newScenes = scenes.map(scene => ({
        ...scene,
        settings: getSettingsWithDefaults(scene.settings)
      }));

      this.scenes = newScenes;
    }

    // Migrate audio to audioClips array
    if (!audioClips && audio) {
      this.audioClips = [
        {
          assetId: audio.assetId,
          type: 'voiceover',
          source: audio.source,
          volume: 1,
          fadeOutDurationSeconds: 0
        }
      ];
    } else if (!audioClips) {
      this.audioClips = [];
    } else {
      this.audioClips = audioClips;
    }
  }

  static mapToElementModel(rawElements) {
    return rawElements
      ? rawElements.map(rawElement => {
          const ElementClass = {
            Text: ScribeTextElementModel,
            Shape: ScribeShapeElementModel,
            Image: ScribeImageElementModel
          }[rawElement.type];

          if (!ElementClass) {
            throw new Error(`Unknown element type ${rawElement.type}`);
          }
          return new ElementClass(rawElement);
        })
      : [];
  }

  static fromObject(scribeObject) {
    const contents = scribeObject.projectModel;

    const createdOnDate = new Date(scribeObject.createdDate);
    const updatedOnDate = scribeObject.modifiedDate ? new Date(scribeObject.modifiedDate) : createdOnDate;

    const model = new ScribeModel({
      id: scribeObject.projectDataId,
      title: contents.title,
      description: contents.description,
      canvasSize: contents.canvasSize,
      thumbnailImage: contents.thumbnailImage,
      createdOn: createdOnDate,
      updatedOn: updatedOnDate,
      rawElements: contents.elements,
      settings: contents.settings,
      audio: contents.audio,
      audioClips: contents.audioClips,
      cursor: contents.cursor,
      source: contents.source,
      scenes: contents.scenes
    });

    model.lastSavedVersion = scribeObject.version;
    model.viewOnly = scribeObject.viewOnly;

    return model;
  }
}

export default ScribeModel;
